import type { PersonSocialNetwork } from "../../../../interfaces/models/IPersonCreate";
import { SocialNetworksEnum, SocialNetworksType } from "../../../../Enums/PersonEnums";

export const PERSON_SOCIAL_FIELD_DEFS = [
  {
    name: "LinkedIn",
    title: "LinkedIn Public Profile URL",
    placeholder: "ex: https://www.linkedin.com/in/username",
  },
  {
    name: "Facebook",
    title: "Facebook Profile URL",
    placeholder: "ex: https://www.facebook.com/yourname",
  },
  {
    name: "WhatsApp",
    title: "WhatsApp Profile URL",
    placeholder: "ex: https://wa.me/usernumber",
  },
  {
    name: "X",
    title: "X/Twitter URL",
    placeholder: "ex: https://www.twitter.com/username",
  },
  {
    name: "Instagram",
    title: "Instagram Address URL",
    placeholder: "ex: https://www.instagram.com/username",
  },
  {
    name: "WeChat",
    title: "WeChat Profile URL",
    placeholder: "ex: https://www.wechat.com/username",
  },
] as const;

export type PersonSocialDisplayName =
  (typeof PERSON_SOCIAL_FIELD_DEFS)[number]["name"];

/**
 * Allowed hostnames by network (exact match or subdomain).
 * Avoids pasting a Facebook URL in the LinkedIn field, for example.
 */
export const SOCIAL_NETWORK_ALLOWED_HOST_SUFFIXES: Record<
  PersonSocialDisplayName,
  readonly string[]
> = {
  LinkedIn: ["linkedin.com", "lnkd.in"],
  Facebook: ["facebook.com"],
  WhatsApp: ["wa.me", "whatsapp.com"],
  X: ["twitter.com", "x.com"],
  Instagram: ["instagram.com"],
  WeChat: ["wechat.com"],
};

function hostnameMatchesSuffix(hostname: string, suffix: string): boolean {
  const h = hostname.toLowerCase();
  const s = suffix.toLowerCase();
  return h === s || h.endsWith(`.${s}`);
}

/**
 * Checks if the URL belongs to the expected domain for that network (e.g. `linkedin.com` in LinkedIn).
 * Empty URLs: true (optional).
 */
export function isSocialUrlForNetwork(
  url: string,
  displayName: PersonSocialDisplayName
): boolean {
  if (!url?.trim()) {
    return true;
  }
  const s = url.trim();
  const candidate = /^https?:\/\//i.test(s)
    ? s
    : `https://${s.replace(/^\/+/, "")}`;
  let hostname: string;
  try {
    hostname = new URL(candidate).hostname;
  } catch {
    return false;
  }
  const suffixes = SOCIAL_NETWORK_ALLOWED_HOST_SUFFIXES[displayName];
  return suffixes.some((suffix) => hostnameMatchesSuffix(hostname, suffix));
}

export type PersonSocialUrlFieldKey = `socialNetworks_${PersonSocialDisplayName}`;

export function socialNetworkErrorKey(
  name: PersonSocialDisplayName
): PersonSocialUrlFieldKey {
  return `socialNetworks_${name}`;
}

export const ALL_SOCIAL_URL_FIELD_KEYS: PersonSocialUrlFieldKey[] =
  PERSON_SOCIAL_FIELD_DEFS.map((d) => socialNetworkErrorKey(d.name));

export type PersonSocialFieldName = (typeof PERSON_SOCIAL_FIELD_DEFS)[number]["name"];

/**
 * Unifica claves legacy (`facebook`, `Facebook`, `linkedIn`) al valor canónico del enum (`Facebook`, `LinkedIn`…).
 * Así `upsert`/`getSocialUrl` no dejan duplicados que mezclen URLs entre campos.
 */
export function canonicalSocialDisplayName(
  raw: string | undefined
): PersonSocialDisplayName | null {
  if (raw == null || String(raw).trim() === "") {
    return null;
  }
  const s = String(raw).trim();
  for (const [, val] of Object.entries(SocialNetworksEnum)) {
    if (val === s) {
      return val as PersonSocialDisplayName;
    }
  }
  for (const [key, val] of Object.entries(SocialNetworksEnum)) {
    if (key === s) {
      return val as PersonSocialDisplayName;
    }
  }
  const lower = s.toLowerCase();
  for (const [key, val] of Object.entries(SocialNetworksEnum)) {
    if (key.toLowerCase() === lower || val.toLowerCase() === lower) {
      return val as PersonSocialDisplayName;
    }
  }
  return null;
}

export const SOCIAL_NETWORK_DEFAULT_ORIGIN: Record<
  PersonSocialDisplayName,
  string
> = {
  LinkedIn: "https://www.linkedin.com",
  Facebook: "https://www.facebook.com",
  WhatsApp: "https://wa.me",
  X: "https://x.com",
  Instagram: "https://www.instagram.com",
  WeChat: "https://www.wechat.com",
};

function tryParseUrl(candidate: string): URL | null {
  try {
    return new URL(candidate);
  } catch {
    return null;
  }
}

function isParseableHttpUrl(value: string): boolean {
  const parsed = tryParseUrl(value);
  if (!parsed) {
    return false;
  }
  return (
    (parsed.protocol === "http:" || parsed.protocol === "https:") &&
    Boolean(parsed.hostname)
  );
}

const LEGACY_SOCIAL_PATH_PREFIXES: Record<
  PersonSocialDisplayName,
  readonly string[]
> = {
  LinkedIn: ["in/", "pub/", "company/", "school/", "showcase/"],
  Facebook: ["profile.php", "people/", "pages/", "groups/"],
  WhatsApp: [],
  X: [],
  Instagram: [],
  WeChat: [],
};

function isCurrentSocialProfileUrl(
  value: string,
  displayName: PersonSocialDisplayName,
): boolean {
  const candidate = /^https?:\/\//i.test(value)
    ? value
    : `https://${value.replace(/^\/+/, "")}`;
  return (
    isSocialUrlForNetwork(value, displayName) && isParseableHttpUrl(candidate)
  );
}

/**
 * Rewrites a stored value that predates the current URL rule.
 * Accepts a host without scheme, a known profile path (`in/name`), or a single handle.
 * Free text stays invalid.
 */
function upgradeDeprecatedSocialUrl(
  rawUrl: string,
  displayName: PersonSocialDisplayName,
): string | null {
  const trimmed = rawUrl.trim();
  if (!trimmed || /\s/.test(trimmed)) {
    return null;
  }

  const path = trimmed.replace(/^\/+/, "");
  const lower = path.toLowerCase();
  const mentionsHost = SOCIAL_NETWORK_ALLOWED_HOST_SUFFIXES[displayName].some(
    (suffix) => lower.includes(suffix.toLowerCase()),
  );

  if (mentionsHost) {
    const resolved = resolveSocialExternalUrl(trimmed, displayName);
    if (
      isParseableHttpUrl(resolved) &&
      isSocialUrlForNetwork(resolved, displayName)
    ) {
      return resolved;
    }
    return null;
  }

  const legacyPrefixes = LEGACY_SOCIAL_PATH_PREFIXES[displayName];
  if (legacyPrefixes.some((prefix) => lower.startsWith(prefix))) {
    const candidate = `${SOCIAL_NETWORK_DEFAULT_ORIGIN[displayName]}/${path}`;
    if (
      isParseableHttpUrl(candidate) &&
      isSocialUrlForNetwork(candidate, displayName)
    ) {
      return candidate;
    }
    return null;
  }

  if (/^@?[A-Za-z0-9._-]{2,}$/.test(path)) {
    const handle = path.replace(/^@/, "");
    const profilePath = displayName === "LinkedIn" ? `in/${handle}` : handle;
    const candidate = `${SOCIAL_NETWORK_DEFAULT_ORIGIN[displayName]}/${profilePath}`;
    if (
      isParseableHttpUrl(candidate) &&
      isSocialUrlForNetwork(candidate, displayName)
    ) {
      return candidate;
    }
  }

  return null;
}

/**
 * Current rule: http(s) URL whose host belongs to that network.
 * Older stored values are rewritten to that shape. Unreadable values return null.
 */
export function canonicalSocialProfileUrl(
  rawUrl: string,
  displayName: PersonSocialDisplayName,
): string | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return "";
  }
  if (isCurrentSocialProfileUrl(trimmed, displayName)) {
    return trimmed;
  }
  return upgradeDeprecatedSocialUrl(trimmed, displayName);
}

function hasKnownSocialHost(
  hostname: string,
  displayName: PersonSocialDisplayName,
): boolean {
  const host = hostname.toLowerCase();
  return SOCIAL_NETWORK_ALLOWED_HOST_SUFFIXES[displayName].some((suffix) =>
    hostnameMatchesSuffix(host, suffix),
  );
}

/**
 * Turns stored social values into a browser-ready URL.
 * Handles full URLs, host-only paths (`linkedin.com/in/foo`), and partial paths (`in/foo`).
 */
export function resolveSocialExternalUrl(
  rawUrl: string,
  displayName: PersonSocialDisplayName,
): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return "";
  }

  if (/^https?:\/\//i.test(trimmed)) {
    const parsed = tryParseUrl(trimmed);
    if (!parsed) {
      return "";
    }
    if (hasKnownSocialHost(parsed.hostname, displayName)) {
      return parsed.toString();
    }
    return trimmed;
  }

  const withoutLeadingSlash = trimmed.replace(/^\/+/, "");
  const lowered = withoutLeadingSlash.toLowerCase();

  for (const suffix of SOCIAL_NETWORK_ALLOWED_HOST_SUFFIXES[displayName]) {
    const hostPrefix = `${suffix}/`;
    const wwwHostPrefix = `www.${suffix}/`;

    if (
      lowered === suffix ||
      lowered.startsWith(hostPrefix) ||
      lowered.startsWith(wwwHostPrefix)
    ) {
      return `https://${withoutLeadingSlash}`;
    }

    const suffixIndex = lowered.indexOf(suffix);
    if (suffixIndex >= 0) {
      return `https://${withoutLeadingSlash.slice(suffixIndex)}`;
    }
  }

  if (/^[^/?#]+\.[^/?#]+/.test(withoutLeadingSlash)) {
    const parsed = tryParseUrl(`https://${withoutLeadingSlash}`);
    if (parsed) {
      return parsed.toString();
    }
  }

  const origin = SOCIAL_NETWORK_DEFAULT_ORIGIN[displayName];
  return `${origin}/${withoutLeadingSlash}`;
}

export function getSocialUrl(
  networks: ReadonlyArray<{ name: string; url: string }> | undefined,
  name: string,
): string {
  const target = canonicalSocialDisplayName(name) ?? name;
  const found = networks?.find((n) => {
    const c = canonicalSocialDisplayName(n.name);
    return c === target || (c == null && n.name === name);
  });
  return found?.url ?? "";
}

export function upsertSocialUrl(
  networks: PersonSocialNetwork[] | undefined,
  nameInput: string,
  url: string
): PersonSocialNetwork[] {
  const canonical = canonicalSocialDisplayName(nameInput);
  const trimmed = url.trim();

  if (!canonical) {
    const rest = (networks ?? []).filter((n) => n.name !== nameInput);
    if (!trimmed) {
      return rest;
    }
    return [
      ...rest,
      { name: nameInput as unknown as SocialNetworksType, url: trimmed },
    ];
  }

  const rest = (networks ?? []).filter((n) => {
    const c = canonicalSocialDisplayName(n.name);
    return c !== canonical;
  });
  if (!trimmed) {
    return rest;
  }
  return [
    ...rest,
    { name: canonical as unknown as SocialNetworksType, url: trimmed },
  ];
}

export function normalizeSocialNetworksFromView(
  networks: PersonSocialNetwork[] | undefined
): PersonSocialNetwork[] | undefined {
  if (!networks?.length) {
    return networks;
  }
  const byCanonical = new Map<PersonSocialDisplayName, PersonSocialNetwork>();
  for (const n of networks) {
    if (!n?.url?.trim()) {
      continue;
    }
    const c = canonicalSocialDisplayName(n.name);
    if (!c) {
      continue;
    }
    const trimmed = n.url.trim();
    byCanonical.set(c, {
      name: c as unknown as SocialNetworksType,
      url: canonicalSocialProfileUrl(trimmed, c) ?? trimmed,
    });
  }
  const merged = [...byCanonical.values()];
  return merged.length ? merged : undefined;
}

export function normalizeSocialNetworksForApi(
  networks: PersonSocialNetwork[] | undefined
): PersonSocialNetwork[] {
  return (networks ?? [])
    .filter((n) => n?.url?.trim())
    .map((n) => {
      const trimmed = n.url.trim();
      const display = canonicalSocialDisplayName(n.name);
      return {
        name: (display ?? n.name) as SocialNetworksType,
        url: display
          ? (canonicalSocialProfileUrl(trimmed, display) ?? trimmed)
          : trimmed,
      };
    });
}
